import { defineStore } from "pinia";
import api from "@/services/api";
import { Countries } from "@/utils/countries";
import type { Facility, FacilityIntensity, FacilityTag } from "@/types";


export interface Filters  {
  name: string | null;
  facilityTypes: FacilityType[] | null;
  tags: string[] | null;
  countryCodes: string[] | null;
  calculationStatus: string | null;
  year: string | null;
  sortBy: string | null;
}

export interface FacilityType  {
  facilityTypeName: string | null;
  facilityTypeLabel: string[] | null;
}

type State = {
  isLoading: boolean;
  facilities: Facility[];
  facilityInstance: FacilityIntensity[];
  totalElements: number;
  page: number;
  nextPagination: boolean;
  totalPages: number;
  size: number;
  selectedTags: string[];
  search: string;
  selectedFacilityId: string | null;
  countries: any[];
  facilityTypes: FacilityType[];
  filters: Filters;
  isWatcherStatus: boolean;
  currentCreatedFacilityId: string | null;
};

export const useCompanyFacilities = defineStore("companyFacilities", {
  state: (): State => ({
    isLoading: false,
    facilities: [] as Facility[],
    facilityInstance: [] as FacilityIntensity[],
    totalElements: 0,
    page: 0,
    nextPagination: false,
    totalPages: 0,
    size: 7,
    selectedTags: [] as string[],
    search: "",
    selectedFacilityId: null,
    countries: [],
    facilityTypes: [],
    filters: {
      name: null,
      facilityTypes: null,
      tags: null,
      countryCodes: null,
      calculationStatus: null,
      year: null,
      sortBy: null
    },
    isWatcherStatus: false,
    currentCreatedFacilityId: null,
  }),
  actions: {
    async getCompanyFacilities(
      page: number | null = null,
      size: number | null = 100,
      onePage: boolean = false,
    ) {
      const companyId = localStorage.getItem("companyId");
      this.isLoading = true;

      if (page !== null && page !== undefined) {
        this.page = page;
      }
      // this.facilities = [];
      if (this.page >= this.totalPages && this.totalPages !== 0)
        return this.facilities;
      if (this.page === 0) {
        this.facilities = [];
      }

      if (
        this.filters.countryCodes &&
        this.filters.countryCodes.length > 0 &&
        !(typeof this.filters.countryCodes[0] === "string")
      ) {
        this.filters.countryCodes = this.filters.countryCodes.map(
          (country: any) => country.code
        );
      }

      try {
        const { data } = await api.get(`/company/${companyId}/facility`, {
          params: {
            page: this.page,
            size: size,
            name: this.filters.name,
            facilityTypes: this.filters.facilityTypes
                ?.map((item) => item.facilityTypeName) // Extract the 'facilityType' field from each object
                .join(","),
            tags: this.filters.tags?.join(","),
            countryCodes: this.filters.countryCodes?.join(","),
            calculationStatus: this.filters.calculationStatus,
            year: this.filters.year,
            sortBy: this.filters.sortBy
          },
        });

        if (onePage) {
          this.facilities = data.content.map((item: Facility) => ({
            ...item,
            facilityName: item.facilityName || "",
            address: item.address || "",
            errors: {},
          }));
        } else {
          const existingIds = new Set(this.facilities.map(f => f.id));
          const newFacilities = data.content
            .filter((item: Facility) => !existingIds.has(item.id))
            .map((item: Facility) => ({
              ...item,
              facilityName: item.facilityName || "",
              address: item.address || "",
              errors: {},
            }));
          this.facilities.push(...newFacilities);
        }

        if (data.content.length === 0) {
          this.selectedFacilityId = null;
        }

        this.totalElements = data.totalElements;
        this.nextPagination = data.first;
        this.totalPages = data.totalPages;
        this.isLoading = false;
        this.page = data.pageable.pageNumber;
      } catch (e) {
        this.isLoading = false;
      }
    },
    async fetchFacilities(page: number = 0) {
      const companyId = localStorage.getItem("companyId");
      if (page >= this.totalPages) {
        return;
      }
      this.page = page;
      this.isLoading = true;
      try {
        const { data } = await api.get(
          `/company/${companyId}/facility?page=${page}&size=${this.size}`
        );

        this.facilities = data.content;
        //this.facilities.push(...data.content)
        this.totalElements = data.totalElements;

        this.isLoading = false;
        this.nextPagination = data.first;
      } catch (e) {
        this.isLoading = false;
      }
    },
    async fetchFacilityCountries() {
      const companyId = localStorage.getItem("companyId");
      try {
        const { data } = await api.get(
          `/company/${companyId}/facility/countries`
        );
        this.countries = data.map((country: string) => {
          return Countries.find((c) => c.code === country);
        });
        return data;
      } catch (e) {}
    },
    async fetchFacilityTypes() {
      const companyId = localStorage.getItem("companyId");
      try {
        const { data } = await api.get(
          `/company/${companyId}/facility/facility-types`
        );
        this.facilityTypes = data;
        return data;
      } catch (e) {}
    },
    updateFacilityItem(facility: Facility) {
      this.facilities = this.facilities.map((i) => {
        i = i.id === facility.id ? facility : i;
        return i;
      });
    },
    updateFacilityTag(facilityId: string, action: string, tag: FacilityTag) {
      // @ts-ignore
      this.facilities = this.facilities.map((facility) => {
        if (action === "add") {
          // @ts-ignore
          facility.tags =
            facility.id === facilityId
              ? [...facility.tags, tag]
              : facility.tags;
        }
        if (action === "delete") {
          // @ts-ignore
          if (facility.id === facilityId) {
            api.delete(`tag/${tag.id}`);
            // @ts-ignore
            const index = facility.tags.findIndex((find) => find.id === tag.id);
            facility.tags.splice(index, 1);
          }
        }

        return facility;
      });
    },
    async updateCompanyFacilities() {
      const companyId = localStorage.getItem("companyId");

      this.isLoading = true;

      await api
        .put(`/company/${companyId}/facility`, this.facilities)
        .then(() => {
          this.isLoading = false;
        })
        .catch((e) => {
          this.isLoading = false;
        });
    },

    async getFacilityInstance(facilityId: string, year?: string) {
      const companyId = localStorage.getItem("companyId");
      try {
        const { data } = await api.get(
          `/company/${companyId}/facility/${facilityId}/intensity?year=${year}`
        );
        this.facilityInstance = data;
        return <FacilityIntensity[] | null>data;
      } catch (e) {
      }
    },
    async createFacilityInstance(facilityId: string, payload: object) {
      this.isLoading = true;
      const companyId = localStorage.getItem("companyId");
      api
        .post(`/company/${companyId}/facility/${facilityId}/intensity`, payload)
        .then(() => {
          this.isLoading = false;
        })
        .catch((e) => {
          this.isLoading = false;
          console.log("err", e);
        });
    },
    async createOrUpdateFacilityInstance(facilityId: string, payload: object) {
      this.isLoading = true;
      const companyId = localStorage.getItem("companyId");
      api
        .put(`/company/${companyId}/facility/${facilityId}/intensity`, payload)
        .then(() => {
          this.isLoading = false;
        })
        .catch((e) => {
          this.isLoading = false;
          console.log("err", e);
        });
    },
    async deleteFacility(facilityId: string) {
      const companyId = localStorage.getItem("companyId");
      this.isLoading = false;
      try {
        await api.delete(`company/${companyId}/facility/${facilityId}`);
        this.totalElements = this.totalElements - 1;

        this.facilities = this.facilities.filter((i) => i.id !== facilityId);
        this.facilities = [...this.facilities];

        this.isLoading = false;
      } catch (e) {
        this.isLoading = false;
        console.log("error", e);
      }
    },
    async updateFacility(facilityId: string, payload: object) {
      this.isLoading = true;
      const companyId = localStorage.getItem("companyId");
      try {
        await api.put(`company/${companyId}/facility/${facilityId}`, payload);
        this.facilities = this.facilities.map(facility => {
          if (facility.id === facilityId) {
            return { ...facility, ...payload } as Facility;
          } else {
            return facility;
          }
        });
        this.isLoading = false;
      } catch (e) {
        this.isLoading = false;
      }
    },
    async createFacility(payload: object) {
      const companyId = localStorage.getItem("companyId");
      this.isLoading = true;
      this.currentCreatedFacilityId = null;
      this.selectedFacilityId = null;
      try {
        const { data } = await api.post(`company/${companyId}/facility/single-facility-create`, payload);
        this.selectedFacilityId = data.id;
        this.currentCreatedFacilityId = data.id;
        this
        this.isLoading = false;
      } catch (e) {
        this.isLoading = false;
        this.currentCreatedFacilityId = null;
        this.selectedFacilityId = null;
        throw e
      }
    }
  },
  getters: {
    getFilteredFacilities({
      facilities,
      selectedTags,
      search,
    }): Facility[] | any {
      // if (selectedTags.length !== 0) {
      //   return facilities
      //     .map((facility) => {
      //       const tags = facility.tags.some((facilityTag) =>
      //         selectedTags.includes(facilityTag.tagName)
      //       );
      //       if (tags) {
      //         return { ...facility, tags };
      //       }
      //       return null;
      //     })
      //     .filter((f) => f);
      // }

      // if (!search) {
      //   return facilities;
      // }

      // return facilities.filter((facility) => {
      //   return facility?.facilityName
      //     ?.toUpperCase()
      //     .includes(search.toUpperCase());
      // });
      return facilities;
    },
    getSelectedFacilities({ facilities, selectedFacilityId }): Facility {
      //@ts-ignore
      return <Facility>(
        facilities.find((facility) => facility.id === selectedFacilityId)
      );
    },
    currentFacilityId({ selectedFacilityId }) {
      return selectedFacilityId;
    },

    getOnboardingFacilities({ facilities }): Facility[] {
      return facilities
    }
  },
});
